import { allCountries } from '@/utils/countries';
import {
  getPostalCodeExample,
  validatePostalCode,
} from '@/utils/postal-code-regex';
import { z } from 'zod';
import { TFunction } from 'i18next';

const validCountryCodes = allCountries.map((country) => country.code);

export const createAddressSchema = (t: TFunction) => {
  return z
    .object({
      addressName2: z
        .string({
          message: t('validation:required'),
        })
        .min(1, {
          message: t('validation:required'),
        })
        .max(60, {
          message: t('validation:maxLength', { count: 60 }),
        }),
      addressName3: z
        .string()
        .max(60, {
          message: t('validation:maxLength', { count: 60 }),
        })
        .optional(),
      street: z
        .string({ message: t('validation:required') })
        .min(1, { message: t('validation:required') })
        .max(60, {
          message: t('validation:maxLength', { count: 60 }),
        }),
      city: z
        .string({ message: t('validation:required') })
        .min(1, { message: t('validation:required') })
        .max(60, {
          message: t('validation:maxLength', { count: 60 }),
        }),
      country: z
        .string({
          error: t('validation:selectCountry'),
        })
        .refine((val) => validCountryCodes.includes(val), {
          message: t('validation:invalidCountry'),
        }),
      zipCode: z
        .string({ message: t('validation:required') })
        .min(1, {
          message: t('validation:required'),
        })
        .max(20, {
          message: t('validation:maxLength', { count: 20 }),
        }),
    })
    .superRefine((data, ctx) => {
      // Validate postal code based on country
      if (data.zipCode && data.country) {
        const isValid = validatePostalCode(data.zipCode, data.country);
        if (!isValid) {
          const example = getPostalCodeExample(data.country);
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t('validation:invalidPostalCode', { example }),
            path: ['zipCode'],
          });
        }
      }
    });
};

export type AddressFormData = z.infer<ReturnType<typeof createAddressSchema>>;
